import type { ActionDefinition } from 'state/actionDefinitions'
import type { IAiModel } from 'state/aiModels'

export type AiMagicWandDraftInputKind = 'text' | 'lyrics' | 'image' | 'video' | 'audio'

export type AiMagicWandDraftImageInput = {
    id: string
    url: string
    label?: string
    file?: File | null
    contentType?: string
    source?: string | null
    uploadId?: string | null
    signedId?: string | null
    attachmentId?: string | null
    blobId?: string | null
    answerId?: string | null
    runId?: string | null
    rootRemixAiRunId?: string | null
    actionId?: string | null
}

export type AiMagicWandDraftVideoInput = AiMagicWandDraftImageInput & {
    previewImageUrl?: string | null
    actionTag?: string | null
    durationSeconds?: number | null
    width?: number | null
    height?: number | null
}

export type AiMagicWandDraftAudioInput = AiMagicWandDraftImageInput & {
    metaLabel?: string | null
    sortTimestamp?: number | null
}

export type AiMagicWandCurrentDraftInputs = {
    text: string
    image: AiMagicWandDraftImageInput | null
    video: AiMagicWandDraftVideoInput | null
    audio: AiMagicWandDraftAudioInput | null
}

export type AiMagicWandDraftModel = Pick<
    ActionDefinition,
    'id' | 'actionTagName' | 'kind' | 'toolKey' | 'modelType'
> &
    Partial<IAiModel>

export type AiMagicWandDraftActionAttrs = Record<string, any>

export function buildDraftInputAttrsForModel({
    model,
    draftInputs,
    supportedInputs,
    effectiveAiModelId,
}: {
    model: AiMagicWandDraftModel
    draftInputs: AiMagicWandCurrentDraftInputs
    supportedInputs: AiMagicWandDraftInputKind[]
    effectiveAiModelId: string | null
}): AiMagicWandDraftActionAttrs {
    const actionKind = model.kind || 'model'
    const attrs: AiMagicWandDraftActionAttrs = {
        tag: model.actionTagName,
        actionDefinitionId: model.id,
        kind: actionKind,
        toolKey: actionKind === 'tool' ? model.toolKey || null : null,
        type: actionKind,
        modelType: model.modelType,
    }
    const inputOrder: string[] = []
    const draftText = draftInputs.text?.trim() || ''

    if (supportedInputs.includes('text') && draftText) {
        attrs.hasTextInput = true
        attrs.textInputValue = draftText
        attrs.promptContent = draftText
        inputOrder.push('text')
    }

    if (model.actionTagName === 'stitch' && draftInputs.video?.url) {
        attrs.videoInputs = [draftInputs.video]
        inputOrder.push('video-0')
    }

    if (supportedInputs.includes('image') && draftInputs.image?.url) {
        attrs.hasImageInput = true
        attrs.imageInputUrl = draftInputs.image.url
        attrs.imageInput = draftInputs.image
        inputOrder.push('image')
    }
    if (supportedInputs.includes('video') && draftInputs.video?.url) {
        attrs.hasVideoInput = true
        attrs.videoInputUrl = draftInputs.video.url
        attrs.videoPreviewImageUrl = draftInputs.video.previewImageUrl || null
        attrs.videoInputActionTag = draftInputs.video.actionTag || null
        attrs.videoInput = draftInputs.video
        inputOrder.push('video')
    }
    if (supportedInputs.includes('audio') && draftInputs.audio) {
        attrs.hasAudioInput = true
        attrs.audioInputUrl = draftInputs.audio.url
        attrs.audioInputSource = draftInputs.audio.source || null
        attrs.audioInputUploadId = draftInputs.audio.uploadId || null
        attrs.audioInputSignedId = draftInputs.audio.signedId || null
        attrs.audioInputLabel = draftInputs.audio.label || null
        attrs.audioInputContentType = draftInputs.audio.contentType || null
        attrs.audioInputFile = draftInputs.audio.file || null
        attrs.audioInput = draftInputs.audio
        inputOrder.push('audio')
    }

    attrs.inputOrder = inputOrder
    attrs.aiModelId = effectiveAiModelId

    return attrs
}
